import { IssueUserInfo } from 'pages/Desk/issues/types';
import { UserResult } from 'pages/Desk/issues/UserResult';
import { Fragment, memo, useMemo, useRef } from 'react';
import { ResultsLoadingIndicator } from 'shared/loading_display/ResultsLoadingIndicator';
import { useLimitMaxHeightToScreenRef } from 'shared/triggered_popup/useLimitMaxHeightToScreenRef';
import { VisibilityProvider } from 'shared/visibility_context/VisibilityContext';
import { VisibilityTrigger } from 'shared/visibility_context/VisibilityTrigger';
import styled from 'styled-components';
import { secondaryText } from 'styles/theme';
import { splitSearchTextIntoTokens } from 'utils/query_v3';

const MaxPopupHeight = '32.5rem';

const ResultsVisibilityProvider = styled(VisibilityProvider)`
    box-sizing: border-box;
    overflow-y: auto;
    overflow-x: hidden;
    display: flex;
    flex-direction: column;
`;

const Message = styled.div`
    padding: 1rem 1.5rem;
    font-size: 1.4rem;
    color: ${secondaryText};
`;

const StyledResultsLoadingIndicator = styled(ResultsLoadingIndicator)`
    padding: 0.5rem;
`;

type UsersPopupContentProps = Readonly<{
    users: IssueUserInfo[] | undefined;
    searchText: string;
    isLoading: boolean;
    hasMore: boolean;
    highlightedIndex: number;
    onBottom: () => void;
    onUserSelect: (user: IssueUserInfo) => void;
}>;

function UsersPopupContent({
    users,
    searchText,
    isLoading,
    hasMore,
    highlightedIndex,
    onBottom,
    onUserSelect,
}: UsersPopupContentProps) {
    const atBottomRef = useRef(false);
    const onVisibilityChange = (isVisible: boolean) => {
        atBottomRef.current = isVisible;
        if (users && isVisible && hasMore && !isLoading) {
            onBottom();
        }
    };

    const searchTokens = useMemo(() => splitSearchTextIntoTokens(searchText), [
        searchText,
    ]);

    const limitMaxHeightRef = useLimitMaxHeightToScreenRef({
        maxHeight: MaxPopupHeight,
    });

    return (
        <>
            {!users && <Message>Search for users</Message>}
            {users && users.length === 0 && <Message>No users found</Message>}
            {users && users.length > 0 && (
                <ResultsVisibilityProvider
                    aria-label="User search results"
                    ref={limitMaxHeightRef}
                >
                    {users.map((user, index) => (
                        <Fragment key={user.id}>
                            {index === users.length - 1 && (
                                <VisibilityTrigger onChange={onVisibilityChange} />
                            )}
                            <UserResult
                                user={user}
                                searchTokens={searchTokens}
                                isHighlighted={index === highlightedIndex}
                                autoScroll
                                onClick={onUserSelect}
                            />
                        </Fragment>
                    ))}
                    {isLoading && atBottomRef.current && (
                        <StyledResultsLoadingIndicator />
                    )}
                </ResultsVisibilityProvider>
            )}
        </>
    );
}

const memoUsersPopupContent = memo(UsersPopupContent);
export { memoUsersPopupContent as UsersPopupContent };
